import { useState, useEffect, useCallback } from 'react';

interface UseQuizPersistenceProps {
    userId: string | undefined;
    courseId: string | undefined;
    quizId: string; // 'final_exam' or specific quiz ID
}

export const useQuizPersistence = <T>({ userId, courseId, quizId }: UseQuizPersistenceProps) => {
    const [key, setKey] = useState<string | null>(null);
    const [questionsKey, setQuestionsKey] = useState<string | null>(null);

    useEffect(() => {
        if (userId && courseId && quizId) {
            setKey(`fastrack_quiz_progress_${userId}_${courseId}_${quizId}`);
            setQuestionsKey(`fastrack_quiz_questions_${userId}_${courseId}_${quizId}`);
        }
    }, [userId, courseId, quizId]);

    const saveProgress = useCallback((data: T) => {
        if (!key) return;
        try {
            localStorage.setItem(key, JSON.stringify(data));
        } catch (error) {
            console.error('Failed to save quiz progress:', error);
        }
    }, [key]);

    const loadProgress = useCallback((): T | null => {
        if (!key) return null;
        try {
            const saved = localStorage.getItem(key);
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            console.error('Failed to load quiz progress:', error);
            return null;
        }
    }, [key]);

    const saveQuestions = useCallback((questions: any[]) => {
        if (!questionsKey) return;
        try {
            localStorage.setItem(questionsKey, JSON.stringify(questions));
        } catch (error) {
            console.error('Failed to save quiz questions:', error);
        }
    }, [questionsKey]);

    const loadQuestions = useCallback((): any[] | null => {
        if (!questionsKey) return null;
        try {
            const saved = localStorage.getItem(questionsKey);
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            console.error('Failed to load quiz questions:', error);
            return null;
        }
    }, [questionsKey]);

    const clearProgress = useCallback(() => {
        if (key) localStorage.removeItem(key);
        if (questionsKey) localStorage.removeItem(questionsKey);
    }, [key, questionsKey]);

    return {
        saveProgress,
        loadProgress,
        saveQuestions,
        loadQuestions,
        clearProgress,
        persistenceKey: key
    };
};
